import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { BrandingProvider } from "@/contexts/BrandingContext";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import DashboardLayout from "@/pages/DashboardLayout";
import DashboardHome from "@/pages/DashboardHome";
import ConnectWhatsApp from "@/pages/ConnectWhatsApp";
import InboxPage from "@/pages/InboxPage";
import BotSettingsPage from "@/pages/BotSettingsPage";
import GroupSettingsPage from "@/pages/GroupSettingsPage";
import FlowsPage from "@/pages/FlowsPage";
import GroupCommandsPage from "@/pages/GroupCommandsPage";
import GroupsPage from "@/pages/GroupsPage";
import BotStudioPage from "@/pages/BotStudioPage";
import MediaLibraryPage from "@/pages/MediaLibraryPage";
import DashboardPlans from "@/pages/DashboardPlans";
import BroadcastPage from "@/pages/BroadcastPage";
import VisualFlowBuilder from "@/pages/flow-builder/VisualFlowBuilder";
import AdminLoginPage from "@/pages/admin/AdminLoginPage";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminClients from "@/pages/admin/AdminClients";
import AdminClientDetail from "@/pages/admin/AdminClientDetail";
import AdminBranding from "@/pages/admin/AdminBranding";
import AdminMaintenance from "@/pages/admin/AdminMaintenance";
import CatalogPage from "@/pages/CatalogPage";
import OrdersPage from "@/pages/OrdersPage";
import PaymentMethodsPage from "@/pages/PaymentMethodsPage";
import KeywordTriggersPage from "@/pages/KeywordTriggersPage";
import CommandsPage from "@/pages/CommandsPage";
import GroupControlsPage from "@/pages/GroupControlsPage";
import LandingPage from "@/pages/landing/LandingPage";
import AdminLanding from "@/pages/admin/AdminLanding";
import AdminSuperAdmins from "@/pages/admin/AdminSuperAdmins";
import AdminEmails from "@/pages/admin/AdminEmails";
import AdminSubscriptions from "@/pages/admin/AdminSubscriptions";
import AdminPaymentProviders from "@/pages/admin/AdminPaymentProviders";
import AdminMyAccount from "@/pages/admin/AdminMyAccount";
import AdminBilling from "@/pages/admin/AdminBilling";
import AdminResellerPlans from "@/pages/admin/AdminResellerPlans";
import AdminResellerSubscriptions from "@/pages/admin/AdminResellerSubscriptions";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { WorkspaceProvider } from "@/contexts/WorkspaceContext";
import WorkspacesPage from "@/pages/WorkspacesPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import BrandHead from "@/components/BrandHead";
import { TenantProvider, useTenant } from "@/contexts/TenantContext";
import { useLocation, useParams } from "react-router-dom";

/** When loaded on a tenant subdomain, replace `/` (landing) with the login page (option B). */
function RootRouteForTenant() {
  const { tenant, loading } = useTenant();
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900" />
      </div>
    );
  }
  if (tenant) return <Navigate to="/login" replace />;
  return <LandingPage />;
}

/**
 * Handles `/t/<slug>/...` URLs. Reads the slug, stores it in sessionStorage via
 * TenantContext (already done in detectSubdomain), then redirects to the real route
 * without the `/t/<slug>` prefix. So `/t/acme/login` → `/login`, and the tenant
 * branding still applies because the slug is now persisted in sessionStorage.
 */
function TenantPathHandler() {
  const params = useParams();
  const location = useLocation();
  const { refresh } = useTenant();
  React.useEffect(() => {
    if (params.tenantSlug) {
      try { sessionStorage.setItem("wa_tenant_slug", params.tenantSlug.toLowerCase()); } catch { /* ignore */ }
      refresh();
    }
    // eslint-disable-next-line
  }, [params.tenantSlug]);
  const rest = location.pathname.replace(/^\/t\/[^/]+/, "") || "/";
  return <Navigate to={rest + location.search} replace />;
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50" data-testid="loading-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900" />
      </div>
    );
  }
  if (user) {
    const plan = new URLSearchParams(window.location.search).get("plan");
    const target = plan ? `/dashboard/plans?plan=${encodeURIComponent(plan)}` : "/dashboard";
    return <Navigate to={target} replace />;
  }
  return children;
}

function App() {
  return (
    <AuthProvider>
      <ThemeProvider>
        <WorkspaceProvider>
        <BrandingProvider>
        <TenantProvider>
          <BrowserRouter>
          <BrandHead />
          <Routes>
            <Route path="/" element={<RootRouteForTenant />} />
            <Route path="/t/:tenantSlug" element={<TenantPathHandler />} />
            <Route path="/t/:tenantSlug/*" element={<TenantPathHandler />} />
            <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
            <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/dashboard" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
              <Route index element={<DashboardHome />} />
              <Route path="connect-whatsapp" element={<ConnectWhatsApp />} />
              <Route path="inbox" element={<InboxPage />} />
              <Route path="bot-studio" element={<BotStudioPage />} />
              <Route path="commands" element={<CommandsPage />} />
              <Route path="catalog" element={<CatalogPage />} />
              <Route path="orders" element={<OrdersPage />} />
              <Route path="payment-methods" element={<PaymentMethodsPage />} />
              <Route path="keywords" element={<KeywordTriggersPage />} />
              <Route path="group-controls" element={<GroupControlsPage />} />
              <Route path="media-library" element={<MediaLibraryPage />} />
              <Route path="plans" element={<DashboardPlans />} />
              <Route path="billing" element={<DashboardPlans />} />
              <Route path="workspaces" element={<WorkspacesPage />} />
              <Route path="broadcast" element={<BroadcastPage />} />
              <Route path="flow-builder" element={<VisualFlowBuilder />} />
              <Route path="flow-builder/:flowId" element={<VisualFlowBuilder />} />
              {/* Legacy routes - backward compat */}
              <Route path="bot-settings" element={<BotSettingsPage />} />
              <Route path="group-settings" element={<GroupSettingsPage />} />
              <Route path="flows" element={<FlowsPage />} />
              <Route path="group-commands" element={<GroupCommandsPage />} />
              <Route path="groups" element={<GroupsPage />} />
            </Route>
            {/* Admin Routes */}
            <Route path="/admin/login" element={<AdminLoginPage />} />
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<Navigate to="/admin/dashboard" replace />} />
              <Route path="dashboard" element={<AdminDashboard />} />
              <Route path="clients" element={<AdminClients />} />
              <Route path="clients/:id" element={<AdminClientDetail />} />
              <Route path="branding" element={<AdminBranding />} />
              <Route path="landing" element={<AdminLanding />} />
              <Route path="emails" element={<AdminEmails />} />
              <Route path="subscriptions" element={<AdminSubscriptions />} />
              <Route path="payment-providers" element={<AdminPaymentProviders />} />
              <Route path="super-admins" element={<AdminSuperAdmins />} />
              <Route path="reseller-plans" element={<AdminResellerPlans />} />
              <Route path="reseller-subscriptions" element={<AdminResellerSubscriptions />} />
              <Route path="me" element={<AdminMyAccount />} />
              <Route path="billing" element={<AdminBilling />} />
              <Route path="maintenance" element={<AdminMaintenance />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        </TenantProvider>
        </BrandingProvider>
        </WorkspaceProvider>
      </ThemeProvider>
    </AuthProvider>
  );
}

export default App;
